import { createRef, useMemo, useState } from 'react';

import { TinderType } from '../../../../../apollo/__generated__/graphql';
import { useCreateTinderMutation } from '../../../../hooks/useCreateTinderMutation';
import { useCurrentUserRemainingSuperLikesQuery } from '../../../../hooks/useCurrentUserRemainingSuperLikesQuery';
import { useMyUntinderedUsersQuery } from '../../../../hooks/useMyUnTinderedUsersQuery';

export type Direction = 'left' | 'right' | 'up' | 'down';

export const useTinderSwipe = (eventId: string) => {
  const { data: myUntinderedUsersData, loading: isUsersLoading } =
    useMyUntinderedUsersQuery(eventId);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const { createTinder } = useCreateTinderMutation();
  const { data: currentUserRemainingSuperLikes } =
    useCurrentUserRemainingSuperLikesQuery(eventId);

  const cards = useMemo(
    () => myUntinderedUsersData?.myUntinderedUsers ?? [],
    [myUntinderedUsersData],
  );

  const childRefs = useMemo(
    () =>
      Array(cards.length)
        .fill(0)
        .map(() =>
          createRef<{
            swipe(dir?: Direction): Promise<void>;
            restoreCard(): Promise<void>;
          }>(),
        ),
    [cards],
  );

  const updateCurrentIndex = () => {
    setCurrentIndex((currentIndex) => currentIndex + 1);
  };

  const canSwipe = currentIndex >= 0 && currentIndex < cards.length;

  const swiped = (direction: Direction) => {
    updateCurrentIndex();
    setCurrentImageIndex(0);
    createTinder({
      eventId,
      type:
        direction === 'right'
          ? TinderType.Like
          : direction === 'up'
            ? TinderType.SuperLike
            : TinderType.Dislike,
      tinderUserId: cards[currentIndex].id,
    });
  };

  const swipe = async (dir: Direction) => {
    if (canSwipe && currentIndex < cards.length) {
      await childRefs[childRefs.length - currentIndex - 1].current?.swipe(dir);
    }
  };

  const currentUser = useMemo(() => cards[currentIndex], [cards, currentIndex]);

  const numberOfImages = useMemo(
    () => (currentUser?.images ?? []).length,
    [currentUser],
  );

  const onNextImagePress = () => {
    setCurrentImageIndex((prev) => (prev + 1) % numberOfImages);
  };

  const onPreviousImagePress = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + numberOfImages) % numberOfImages,
    );
  };

  const hasSeenAllProfiles = useMemo(
    () => currentIndex >= cards.length,
    [currentIndex, cards],
  );

  const remainingSuperLikes =
    currentUserRemainingSuperLikes?.currentUser?.remainingSuperLikes ?? 0;

  return {
    swiped,
    swipe,
    canSwipe,
    cards,
    childRefs,
    onNextImagePress,
    onPreviousImagePress,
    currentImageIndex,
    numberOfImages,
    hasSeenAllProfiles,
    currentIndex,
    isUsersLoading,
    remainingSuperLikes,
  };
};
